import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Play, ChevronRight } from "lucide-react";
import { http } from "@/lib/api";
import { fetchEvents, fetchGalleryImages } from "@/api/events";
import { ASSETS, MATHA } from "@/lib/constants";
import { useLang } from "@/i18n";
import MandalaDivider from "@/components/MandalaDivider";
import SectionHeading from "@/components/SectionHeading";
import { youtubeVideos } from "@/mockData";

const QUICK_DEF = [
  { key: "purana", to: "/daily-events", img: ASSETS.quickLinks.purana },
  { key: "goshala", to: "/sevas", img: ASSETS.quickLinks.goshala },
  { key: "bruhathi", to: "/sevas", img: ASSETS.quickLinks.bruhathi },
  { key: "aradhana", to: "/events", img: ASSETS.quickLinks.aradhana },
  { key: "daily", to: "/daily-events", img: ASSETS.quickLinks.dailyEvents },
  { key: "book", to: "/sevas", img: ASSETS.quickLinks.bookSeva },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);
  const { t, lv, fmtDate, lang } = useLang();
  const isIndic = lang !== "en";

  useEffect(() => {
    fetchEvents({ size: 3 }).then(setEvents).catch(() => {});
    fetchGalleryImages({ size: 6 }).then(setGallery).catch(() => {});
    setVideos(youtubeVideos.videos || []);
  }, []);

  return (
    <div data-testid="home-page">
      {/* ---------- Hero ---------- */}
      <section className="relative bg-[#FDFBF7] px-3 sm:px-4 lg:px-6 pt-4">
        <div className="relative overflow-hidden rounded-3xl ring-1 ring-[#E5DAC7] shadow-[0_10px_40px_rgba(60,40,20,0.06)]">
        <div className="absolute inset-0">
          <img
            src={ASSETS.heroBanner}
            alt="Copper Rama-Sita-Lakshmana vigrahas amidst temple line-art"
            className="w-full h-full object-cover object-right"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(253,251,247,0.92) 0%, rgba(253,251,247,0.72) 32%, rgba(253,251,247,0.20) 55%, rgba(253,251,247,0.00) 72%)",
            }}
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[#FDFBF7]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="max-w-xl">
            <div className="eyebrow">{t.ribbon.tagline}</div>
            <h1
              data-testid="hero-title"
              className={`mt-4 font-display text-[#2A2118] ${
                isIndic
                  ? "text-2xl sm:text-3xl lg:text-[40px] leading-[1.35]"
                  : "text-4xl sm:text-5xl lg:text-6xl leading-[1.02]"
              }`}
            >
              {t.home.welcome} <br className="hidden sm:block"/>
              <span className={`text-[#7B1818] ${isIndic ? "block mt-3" : ""}`}>{t.matha.name}</span>
            </h1>
            <div className="mt-3 text-[12px] uppercase tracking-[0.24em] text-[#8a6a3a] font-semibold">
              {t.matha.aka}
            </div>
            <p className="mt-5 text-[15px] text-[#5C4D42] max-w-lg leading-relaxed">
              {t.home.heroPara}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/about" data-testid="hero-explore-btn" className="btn-primary">
                {t.home.explore} <ArrowRight size={16} />
              </Link>
              <Link to="/sevas" data-testid="hero-book-seva-btn" className="btn-outline">
                {t.home.bookSeva}
              </Link>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* ---------- Pontiffs ---------- */}
      <section className="section-pad" data-testid="pontiffs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow={t.home.pontiffsEyebrow}
            title={t.home.pontiffsTitle}
            sub={t.home.pontiffsSub}
          />

          <div className="mt-14 grid md:grid-cols-2 gap-8">
            <article data-testid="pontiff-previous-card" className="card-soft reveal p-8 flex flex-col items-center text-center">
              <div className="eyebrow">{t.home.previousPontiff}</div>
              <div className="mt-6 w-52 h-52 rounded-full overflow-hidden ring-1 ring-[#E5DAC7] shadow-md">
                <img src={ASSETS.previousPontiff} alt={t.home.prevName} className="w-full h-full object-cover" />
              </div>
              <h3 className="mt-6 font-display text-2xl lg:text-3xl text-[#7B1818]">
                {t.home.prevName}
              </h3>
              <p className="mt-3 text-[14.5px] text-[#5C4D42] leading-relaxed max-w-md">
                {t.home.prevDesc}
              </p>
              <Link to="/parampara" data-testid="pontiff-previous-link" className="btn-ghost mt-5">
                {t.home.viewParampara} <ChevronRight size={14} />
              </Link>
            </article>

            <article data-testid="pontiff-current-card" className="card-soft reveal p-8 flex flex-col items-center text-center relative">
              <span className="absolute top-6 right-6 text-[10px] tracking-[0.22em] uppercase bg-[#7B1818] text-[#F4EBDB] px-3 py-1 rounded-full">
                {t.home.current}
              </span>
              <div className="eyebrow">{t.home.currentPontiff}</div>
              <div className="mt-6 w-52 h-52 rounded-full overflow-hidden ring-2 ring-[#D4AF37] shadow-lg">
                <img src={ASSETS.currentPontiff} alt={t.home.currName} className="w-full h-full object-cover" />
              </div>
              <h3 className="mt-6 font-display text-2xl lg:text-3xl text-[#7B1818]">
                {t.home.currName}
              </h3>
              <p className="mt-3 text-[14.5px] text-[#5C4D42] leading-relaxed max-w-md">
                {t.home.currDesc}
              </p>
              <Link to="/parampara" data-testid="pontiff-current-link" className="btn-ghost mt-5">
                {t.home.viewParampara} <ChevronRight size={14} />
              </Link>
            </article>
          </div>
        </div>
      </section>

      {/* ---------- About preview ---------- */}
      <section className="section-pad bg-[#F4EBDB]/40 border-y border-[#E5DAC7]/70" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal relative">
            <div className="absolute -inset-4 bg-[#E6D5B8]/40 rounded-3xl -z-10" />
            <img
              src={ASSETS.ourHeritage}
              alt="Our Heritage — Sri Bhimanakatte Matha"
              className="w-full h-auto object-contain rounded-2xl shadow-[0_20px_50px_rgba(60,40,20,0.15)] bg-[#FDFBF7]"
            />
          </div>

          <div className="reveal">
            <div className="eyebrow">{t.home.heritageEyebrow}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#2A2118] leading-[1.1]">
              {t.home.heritageTitle1}<br/>
              <span className="text-[#7B1818]">{t.home.heritageTitle2}</span>
            </h2>
            <div className="mt-6"><MandalaDivider /></div>
            <p className="mt-6 text-[15.5px] text-[#5C4D42] leading-[1.85]">
              {t.home.heritagePara1}
            </p>
            <p className="mt-4 text-[15.5px] text-[#5C4D42] leading-[1.85]">
              {t.home.heritagePara2}
            </p>

            <div className="mt-8 flex gap-3">
              <Link to="/about" data-testid="about-read-more-btn" className="btn-primary">
                {t.common.readMore} <ArrowRight size={16} />
              </Link>
              <Link to="/parampara" data-testid="about-parampara-btn" className="btn-outline">
                {t.home.guruParampara}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Upcoming Events ---------- */}
      <section className="section-pad" data-testid="events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="max-w-xl">
              <div className="eyebrow">{t.home.eventsEyebrow}</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#2A2118] leading-[1.1]">
                {t.home.eventsTitle}
              </h2>
              <p className="mt-4 text-[15px] text-[#5C4D42]">
                {t.home.eventsSub}
              </p>
            </div>
            <Link to="/events" data-testid="events-view-all-btn" className="btn-ghost">
              {t.home.viewAllEvents} <ChevronRight size={14} />
            </Link>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((e, i) => (
              <article key={e.id} data-testid={`event-card-${i}`} className="card-soft reveal overflow-hidden flex flex-col">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={e.image} alt={lv(e, "title")} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-[11px] font-semibold text-[#7B1818] flex items-center gap-1.5">
                    <Calendar size={12} /> {fmtDate(e.date, { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-xl text-[#2A2118]">{lv(e, "title")}</h3>
                  <p className="mt-1 text-[12.5px] text-[#8a6a3a] flex items-center gap-1"><MapPin size={12} /> {lv(e, "location")}</p>
                  <p className="mt-3 text-[14px] text-[#5C4D42] leading-relaxed line-clamp-3">{lv(e, "description")}</p>
                  <Link to="/events" className="btn-ghost mt-5 self-start">
                    {t.common.readMore} <ChevronRight size={14} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Latest Videos ---------- */}
      <section className="section-pad bg-[#F4EBDB]/40 border-y border-[#E5DAC7]/70" data-testid="videos-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow={t.home.videosEyebrow}
            title={t.home.videosTitle}
            sub={t.home.videosSub}
          />
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {videos.map((v, i) => (
              <a
                key={v.id}
                href={v.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`video-card-${i}`}
                className="card-soft reveal overflow-hidden group"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors flex items-center justify-center">
                    <span className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                      <Play className="text-[#7B1818] ml-0.5" size={22} fill="#7B1818" />
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg text-[#2A2118] leading-snug line-clamp-2">{v.title}</h3>
                  <p className="mt-2 text-[12px] text-[#8a6a3a]">{v.published}</p>
                </div>
              </a>
            ))}
          </div>
          <div className="text-center mt-10">
            <a
              href={MATHA.youtube}
              target="_blank"
              rel="noreferrer"
              data-testid="youtube-channel-btn"
              className="btn-outline"
            >
              {t.home.visitYT}
            </a>
          </div>
        </div>
      </section>

      {/* ---------- Gallery preview ---------- */}
      <section className="section-pad" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <div className="eyebrow">{t.home.galleryEyebrow}</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#2A2118] leading-[1.1]">{t.home.galleryTitle}</h2>
            </div>
            <Link to="/gallery" data-testid="gallery-view-all-btn" className="btn-ghost">{t.home.viewGallery} <ChevronRight size={14} /></Link>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((g, i) => (
              <div key={g.id} data-testid={`gallery-tile-${i}`} className={`reveal overflow-hidden rounded-2xl bg-[#E5DAC7] ${i === 0 ? "col-span-2 md:col-span-2 md:row-span-2 aspect-[4/3] md:aspect-auto" : "aspect-square"}`}>
                <img src={g.image} alt={lv(g, "title")} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Quick Links ---------- */}
      <section className="section-pad bg-[#F4EBDB]/40 border-y border-[#E5DAC7]/70" data-testid="quick-links-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow={t.home.quickEyebrow} title={t.home.quickTitle} />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {QUICK_DEF.map((q) => (
              <Link
                key={q.key}
                to={q.to}
                data-testid={`quick-link-${q.key}`}
                className="card-soft reveal overflow-hidden group"
              >
                <div className="flex">
                  <div className="w-28 h-28 shrink-0 overflow-hidden">
                    <img src={q.img} alt={t.home.quick[q.key].label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <div className="p-5 flex-1">
                    <h3 className="font-display text-xl text-[#2A2118]">{t.home.quick[q.key].label}</h3>
                    <p className="mt-1 text-[13px] text-[#5C4D42] line-clamp-2">{t.home.quick[q.key].desc}</p>
                    <span className="mt-3 inline-flex items-center text-[#7B1818] text-[13px] font-semibold gap-1">
                      {t.common.go} <ChevronRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Book Seva Banner ---------- */}
      <section className="section-pad" data-testid="book-seva-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal relative overflow-hidden rounded-3xl bg-[#7B1818] text-[#F4EBDB]">
            <div className="absolute inset-0 opacity-30">
              <img src={ASSETS.deity1} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#7B1818] via-[#7B1818]/85 to-transparent" />
            </div>
            <div className="relative grid lg:grid-cols-5 gap-8 p-10 lg:p-16 items-center">
              <div className="lg:col-span-3">
                <div className="eyebrow text-[#D4AF37]">{t.home.ctaEyebrow}</div>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                  {t.home.ctaTitle}
                </h2>
                <p className="mt-5 text-[15px] text-[#F4EBDB]/85 leading-relaxed max-w-xl">
                  {t.home.ctaPara}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/sevas" data-testid="banner-book-seva-btn" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#D4AF37] text-[#3A2410] font-semibold hover:bg-[#e5c04a] transition-colors">
                    {t.home.browseSevas} <ArrowRight size={16} />
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-[#F4EBDB]/60 text-[#F4EBDB] font-semibold hover:bg-white/10">
                    {t.home.contactMatha}
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block lg:col-span-2">
                <div className="relative aspect-square rounded-2xl overflow-hidden ring-2 ring-[#D4AF37]/50">
                  <img src={ASSETS.deity3} alt="Sacred idols" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
